// Barrel for on-page imagery.
//
// These are imported (not referenced as /public paths) so Vite content-hashes
// them into /assets/, which is the only way they get a long immutable cache —
// files served straight out of public/ come back with no cache-control header
// at all. The sources were re-encoded from ~488 KB PNGs to WebP at their native
// 500px dimensions; pixel size is unchanged, so nothing renders differently.
import aboutHero from "./about-hero.webp";
import airbnbCleaning from "./airbnb-cleaning.webp";
import apartmentCleaning from "./apartment-cleaning.webp";
import blogAirbnbTurnaround from "./blog-airbnb-turnaround.webp";
import blogCarpetCare from "./blog-carpet-care.webp";
import blogDeepCleaningChecklist from "./blog-deep-cleaning-checklist.webp";
import blogHero from "./blog-hero.webp";
import blogMoveOutGuide from "./blog-move-out-guide.webp";
import blogWindowCleaning from "./blog-window-cleaning.webp";
import carpetCleaning from "./carpet-cleaning.webp";
import commercialCleaning from "./commercial-cleaning.webp";
import constructionCleaning from "./construction-cleaning.webp";
import deepCleaning from "./deep-cleaning.webp";
import heroHome from "./hero-home.webp";
import houseCleaning from "./house-cleaning.webp";
import janitorialCleaning from "./janitorial-cleaning.webp";
import logo from "./goodchoice-logo.webp";
import moveInCleaning from "./move-in-cleaning.webp";
import moveOutCleaning from "./move-out-cleaning.webp";
import officeCleaning from "./office-cleaning.webp";
import regularCleaning from "./regular-cleaning.webp";
import residentialCleaning from "./residential-cleaning.webp";
import servicesHero from "./services-hero.webp";
import turnsCleaning from "./turns-cleaning.webp";
import whyChooseShowcase from "./why-choose-showcase.webp";
import windowsCleaning from "./windows-cleaning.webp";

// 360px-wide variants for the card grids. The cards render at roughly 355px,
// so shipping only the 500px encode meant every card image was bigger than its
// slot — Lighthouse flagged all 15 of them. Heroes are excluded: they stretch
// full-bleed and always want the largest encode available.
import airbnbCleaning360 from "./airbnb-cleaning-360.webp";
import apartmentCleaning360 from "./apartment-cleaning-360.webp";
import blogAirbnbTurnaround360 from "./blog-airbnb-turnaround-360.webp";
import blogCarpetCare360 from "./blog-carpet-care-360.webp";
import blogDeepCleaningChecklist360 from "./blog-deep-cleaning-checklist-360.webp";
import blogMoveOutGuide360 from "./blog-move-out-guide-360.webp";
import blogWindowCleaning360 from "./blog-window-cleaning-360.webp";
import carpetCleaning360 from "./carpet-cleaning-360.webp";
import commercialCleaning360 from "./commercial-cleaning-360.webp";
import constructionCleaning360 from "./construction-cleaning-360.webp";
import deepCleaning360 from "./deep-cleaning-360.webp";
import houseCleaning360 from "./house-cleaning-360.webp";
import janitorialCleaning360 from "./janitorial-cleaning-360.webp";
import moveInCleaning360 from "./move-in-cleaning-360.webp";
import moveOutCleaning360 from "./move-out-cleaning-360.webp";
import officeCleaning360 from "./office-cleaning-360.webp";
import regularCleaning360 from "./regular-cleaning-360.webp";
import residentialCleaning360 from "./residential-cleaning-360.webp";
import turnsCleaning360 from "./turns-cleaning-360.webp";
import whyChooseShowcase360 from "./why-choose-showcase-360.webp";
import windowsCleaning360 from "./windows-cleaning-360.webp";

export const img = {
  aboutHero,
  airbnbCleaning,
  apartmentCleaning,
  blogAirbnbTurnaround,
  blogCarpetCare,
  blogDeepCleaningChecklist,
  blogHero,
  blogMoveOutGuide,
  blogWindowCleaning,
  carpetCleaning,
  commercialCleaning,
  constructionCleaning,
  deepCleaning,
  heroHome,
  houseCleaning,
  janitorialCleaning,
  logo,
  moveInCleaning,
  moveOutCleaning,
  officeCleaning,
  regularCleaning,
  residentialCleaning,
  servicesHero,
  turnsCleaning,
  whyChooseShowcase,
  windowsCleaning,
} as const;

// Every source image is 500x333 except these two. Declaring intrinsic
// width/height on each <img> is what stops the layout shifting while they load.
export const IMG_W = 500;
export const IMG_H = 333;
export const IMG_DIMS: Record<string, { w: number; h: number }> = {
  [houseCleaning]: { w: 500, h: 332 },
  [moveInCleaning]: { w: 500, h: 347 },
};

export const dimsFor = (src: string) => IMG_DIMS[src] ?? { w: IMG_W, h: IMG_H };

// Keyed by the 500px URL so callers can go straight from the src they already
// hold to a srcset. Byte-identical sources (airbnb/deep-cleaning,
// regular/blog-deep-cleaning-checklist/blog-move-out-guide,
// janitorial/why-choose-showcase) collapse to one Vite asset each, so several
// of these keys are the same string — harmless, they resolve identically.
const SRCSET: Record<string, string> = {
  [airbnbCleaning]: `${airbnbCleaning360} 360w, ${airbnbCleaning} 500w`,
  [apartmentCleaning]: `${apartmentCleaning360} 360w, ${apartmentCleaning} 500w`,
  [blogAirbnbTurnaround]: `${blogAirbnbTurnaround360} 360w, ${blogAirbnbTurnaround} 500w`,
  [blogCarpetCare]: `${blogCarpetCare360} 360w, ${blogCarpetCare} 500w`,
  [blogDeepCleaningChecklist]: `${blogDeepCleaningChecklist360} 360w, ${blogDeepCleaningChecklist} 500w`,
  [blogMoveOutGuide]: `${blogMoveOutGuide360} 360w, ${blogMoveOutGuide} 500w`,
  [blogWindowCleaning]: `${blogWindowCleaning360} 360w, ${blogWindowCleaning} 500w`,
  [carpetCleaning]: `${carpetCleaning360} 360w, ${carpetCleaning} 500w`,
  [commercialCleaning]: `${commercialCleaning360} 360w, ${commercialCleaning} 500w`,
  [constructionCleaning]: `${constructionCleaning360} 360w, ${constructionCleaning} 500w`,
  [deepCleaning]: `${deepCleaning360} 360w, ${deepCleaning} 500w`,
  [houseCleaning]: `${houseCleaning360} 360w, ${houseCleaning} 500w`,
  [janitorialCleaning]: `${janitorialCleaning360} 360w, ${janitorialCleaning} 500w`,
  [moveInCleaning]: `${moveInCleaning360} 360w, ${moveInCleaning} 500w`,
  [moveOutCleaning]: `${moveOutCleaning360} 360w, ${moveOutCleaning} 500w`,
  [officeCleaning]: `${officeCleaning360} 360w, ${officeCleaning} 500w`,
  [regularCleaning]: `${regularCleaning360} 360w, ${regularCleaning} 500w`,
  [residentialCleaning]: `${residentialCleaning360} 360w, ${residentialCleaning} 500w`,
  [turnsCleaning]: `${turnsCleaning360} 360w, ${turnsCleaning} 500w`,
  [whyChooseShowcase]: `${whyChooseShowcase360} 360w, ${whyChooseShowcase} 500w`,
  [windowsCleaning]: `${windowsCleaning360} 360w, ${windowsCleaning} 500w`,
};

export const srcSetFor = (src: string): string | undefined => SRCSET[src];

// Card grids: ~1 column on phones, 2 from sm, 3 from lg capped by the container.
// Measured: the card image is 355 CSS px at desktop widths. This has to be
// declared at (not above) the real render width — at 380px the browser needs
// 380 physical px at DPR 1, rejects the 360w candidate, and downloads the 500w
// one anyway, which is exactly what happened on the first attempt.
export const CARD_SIZES = "(min-width: 1024px) 355px, (min-width: 640px) 45vw, 92vw";

// The on-page logo is served at 130x128 (it renders at 40-64px, so the original
// 505px source was ~8x more pixels than any call site used). Kept at 2x the
// largest render rather than 1x: Lighthouse measures "displayed dimensions" at
// DPR 1 and would have us ship 65px, which is visibly soft on any retina screen.
export const LOGO_W = 130;
export const LOGO_H = 128;
